import React, { useEffect, useState } from 'react';
import { MessageList } from './ChatArea/MessageList';
import { ChatInput } from './ChatArea/ChatInput';
import { useChatStore } from '../store/chatStore';
import { useWebSocketStore } from '../../../store/websocketStore';
import { useUserProfile } from '../../../api/profile';
import {
  MEMORY_QUERY_KEY,
  useMemory,
  useUpdateMemory,
} from '../../../api/memory';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@plerionhq/design-system';
import { ChatIcon } from '../../onboarding/screens/LandingScreen';
import { useNavigate } from '@tanstack/react-router';

import { useTranslation } from 'react-i18next';

/**
 * UserQuestionsChat is a simple chat area for user preferences.
 * It manages its own messages state and renders a message list and input.
 */
export const UserQuestionsChat: React.FC = () => {
  const { t } = useTranslation('pleri/chat');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isInvalidatingMemory, setIsInvalidatingMemory] = useState(false);
  const addMessage = useChatStore((state) => state.addMessage);
  const { data: userProfile, isLoading: isUserProfileLoading } =
    useUserProfile();
  const userId = userProfile?.id;
  const conversationExternalId = userId && `question-chat-user-${userId}`;
  const wsStatus = useWebSocketStore((state) => state.status);
  const { data: memory, isLoading: isMemoryLoading } = useMemory({
    refetchOnWindowFocus: false,
  });
  const preferencesSetupComplete = memory?.answeredAllOnboardingQuestions;
  const queryClient = useQueryClient();
  const updateMemoryMutation = useUpdateMemory();
  const activeConversationId = useChatStore(
    (state) => state.activeConversationId,
  );
  const activeConversation = useChatStore((state) =>
    activeConversationId
      ? state.conversations[activeConversationId]
      : undefined,
  );

  // Refetch memory after each assistant message in preference chat
  useEffect(() => {
    if (wsStatus !== 'OPEN') {
      return;
    }
    if (isUserProfileLoading || !userProfile || !conversationExternalId) {
      return;
    }
    if (
      activeConversation?.conversationExternalId?.startsWith(
        'question-chat-user-',
      ) &&
      activeConversation.messages.length > 0
    ) {
      const lastMessage =
        activeConversation.messages[activeConversation.messages.length - 1];
      if (
        lastMessage.role === 'assistant' &&
        activeConversation.isLoading === false
      ) {
        queryClient.invalidateQueries({ queryKey: MEMORY_QUERY_KEY });
      }
    }
    // Only run when the number of messages or loading state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeConversation?.messages.length,
    activeConversation?.isLoading,
    wsStatus,
    isUserProfileLoading,
    userProfile,
    conversationExternalId,
  ]);

  useEffect(() => {
    if (wsStatus !== 'OPEN') {
      return;
    }
    if (isUserProfileLoading || !userProfile || !conversationExternalId) {
      return;
    }
    const initializeConversation = async () => {
      const conversation = await useChatStore
        .getState()
        .findConversationIdByExternalId(conversationExternalId);

      let conversationId = conversation?.conversationId;
      if (!conversationId) {
        conversationId = useChatStore.getState().createConversation({
          conversationExternalId,
        });
        addMessage(conversationId, {
          // A prompt to the agent, kept in the conversation: not UI text.
          content: [{ type: 'text', text: 'Hi' }],
          role: 'user',
        });
      } else {
        useChatStore.getState().setActiveConversation({
          conversationId,
        });
      }
      useChatStore.getState().updateConversationState(conversationId, {
        hideIncludeContext: true,
        hideToolsBadge: true,
        pendingToolUses: new Set<string>(),
        hideToolUse: true,
      });
      setIsLoading(false);
    };
    initializeConversation();
  }, [
    addMessage,
    conversationExternalId,
    wsStatus,
    isUserProfileLoading,
    userProfile,
  ]);

  const handleSkip = async () => {
    try {
      await updateMemoryMutation.mutateAsync({
        skippedOnboardingQuestions: true,
      });
      await queryClient.refetchQueries({ queryKey: ['main-memory'] });
      useChatStore.getState().resetState();
      navigate({ to: '/c/$conversationId', params: { conversationId: 'new' } });
    } catch (error) {
      console.error('Failed to skip preference setup:', error);
    }
  };
  const onComplete = async () => {
    setIsInvalidatingMemory(true);
    try {
      await queryClient.refetchQueries({ queryKey: ['main-memory'] });
      useChatStore.getState().resetState();
    } finally {
      setIsInvalidatingMemory(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[9999999999] transition-all duration-300 ${'pointer-events-auto opacity-100'}`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-900/10 backdrop-blur-sm" />
      {/* Chat Container */}
      <div className="absolute inset-4 flex flex-col rounded-xl bg-white/95 shadow-2xl backdrop-blur-sm dark:bg-gray-900/95">
        {isUserProfileLoading || isMemoryLoading ? (
          <div className="flex h-full w-full items-center justify-center bg-white/0 dark:bg-gray-900/0">
            <span className="text-lg text-gray-500 dark:text-gray-400">
              {t('common:loading')}
            </span>
          </div>
        ) : (
          <>
            {' '}
            <div className="flex items-center justify-between rounded-t-xl border-b border-gray-200/50 bg-white/80 px-6 py-4 dark:border-gray-700/50 dark:bg-gray-900/80">
              <div>
                <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                  {t('iHaveAFewQuestionsForYou')}
                </div>
                <div className="mt-1 block text-sm text-gray-500 dark:text-gray-400">
                  {t('iMExcitedToJoinTheTeam')}
                </div>
              </div>

              {!preferencesSetupComplete && (
                <Button
                  onClick={handleSkip}
                  variant="primary"
                  isLoading={updateMemoryMutation.isPending}
                  isDisabled={updateMemoryMutation.isPending}
                  label={t('skip')}
                />
              )}
            </div>
            {/* End Top Toolbar */}
            {isLoading ? (
              <div className="flex h-full w-full items-center justify-center bg-white/0 dark:bg-gray-900/0">
                <span className="text-lg text-gray-500 dark:text-gray-400">
                  {t('common:loading')}
                </span>
              </div>
            ) : (
              <div className="flex h-full min-h-0 w-full flex-col bg-white/0 dark:bg-gray-900/0">
                <div className="min-h-0 flex-1 overflow-y-auto p-4">
                  <div className="mx-auto max-w-3xl">
                    <MessageList showMessageFromIndex={1} />
                  </div>
                </div>
                {!preferencesSetupComplete ? (
                  <div className="sticky bottom-0 w-full bg-white/0 px-4 pt-4 pb-1 dark:bg-gray-900/0">
                    <div className="mx-auto w-full max-w-3xl">
                      <ChatInput />
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-center gap-4 p-10">
                    <Button
                      onClick={async () => {
                        await onComplete();
                        navigate({
                          to: '/c/$conversationId',
                          params: { conversationId: 'new' },
                        });
                      }}
                      variant="primary"
                      isLoading={isInvalidatingMemory}
                      icon={<ChatIcon className="h-5 w-5" />}
                      label={t('letGetToWork')}
                    />
                    <Button
                      onClick={async () => {
                        await onComplete();
                        navigate({ to: '/trigger' });
                      }}
                      variant="secondary"
                      isLoading={isInvalidatingMemory}
                      className="border-action-primary text-action-primary hover:bg-action-primary/10 border"
                      label={t('close')}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
